import { Component, Inject } from '@angular/core';
import { ExecutedPaymentBatch } from '../../models';
import { compareStringsIgnoreCase, DialogSize, LoadableStatus, removeDuplicates } from '@aiwyn/shared';
import { PaymentsState } from '../../state';
import { Store } from '@ngxs/store';
import { ApplyCreditsPayload, ChargePayload } from '@aiwyn/api';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { isAuthFailure, resolveDeclineMessage, SESSION_EXPIRED_CHARGE_MESSAGE } from '../../utils';

export interface PaymentResultsDialogData {
  payments: ExecutedPaymentBatch;
  primaryEmail: string;
  isQuickPayment?: boolean;
  // errorMessage?: string;
}

interface PaymentResultDisplay {
  name: string;
  amount: number;
}

// Specific error messages that require custom content in the dialog
enum CustomContentErrorMessage {
  ACH_DISPUTE = 'This payment method previously had an ACH Block or Dispute associated with it and needs to be verified for further transactions.',
}

@Component({
  selector: 'app-payment-results-dialog',
  templateUrl: './payment-results-dialog.component.html',
  styleUrls: ['./payment-results-dialog.component.scss'],
  standalone: false,
})
export class PaymentResultsDialogComponent {
  readonly DialogSize = DialogSize;
  readonly CustomContentErrorMessage = CustomContentErrorMessage;

  initialPaymentFailed: boolean;
  successfulPayments: PaymentResultDisplay[];
  failedPayments: PaymentResultDisplay[];

  errorMessageIncludesCustomContent: boolean;
  confirmButtonText = 'Close';
  displayMessage: string | undefined;
  // PP-3820: a 401 on the charge is an expired/missing session, not a decline. When true the dialog
  // shows the session-expired copy and its confirm button reloads the page instead of just closing.
  isSessionExpired = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PaymentResultsDialogData,
    private readonly store: Store,
  ) {
    if (this.data.payments.initial.status === LoadableStatus.ERROR) {
      this.initialPaymentFailed = true;

      // PP-3820: intercept auth failures before the decline-message path so a 401 is never labeled
      // "your card has been declined" (it also hits ACH/bank payments). Offer a reload to recover.
      if (isAuthFailure(this.data.payments.initial.error)) {
        this.isSessionExpired = true;
        this.displayMessage = SESSION_EXPIRED_CHARGE_MESSAGE;
        this.confirmButtonText = 'Reload page';
        return;
      }

      this.errorMessageIncludesCustomContent = Object.values<string>(CustomContentErrorMessage)
        .includes(this.data.payments.initial.error?.message);

      this.confirmButtonText = this.data.payments.initial.error?.message === CustomContentErrorMessage.ACH_DISPUTE
        ? 'Try a different payment method'
        : 'Try again';

      this.displayMessage = resolveDeclineMessage(
        this.data.payments.initial.error?.declineCode,
        this.data.payments.initial.error?.message,
      );

      return;
    }

    const initialPayment = this.getInitialPayloadDisplay(this.data.payments.initial.data);

    const successfulAddOnPayments = this.getAddOnPayments(this.data.payments, LoadableStatus.LOADED);
    const failedAddOnPayments = this.getAddOnPayments(this.data.payments, LoadableStatus.ERROR);

    this.successfulPayments = [initialPayment].concat(successfulAddOnPayments);
    this.failedPayments = failedAddOnPayments;
  }

  getInitialPayloadDisplay(initialPayload: ChargePayload | ApplyCreditsPayload): PaymentResultDisplay {
    if ('chargeDetails' in initialPayload) {
      return {
        name: `Invoice${initialPayload.chargeDetails.arTranAllocations.length > 1 ? 's' : ''}`,
        amount: initialPayload.chargeDetails.paymentDetails.amount,
      };
    }

    return {
      name: `Invoice${removeDuplicates(initialPayload.allocations.map(({ arTranId }) => arTranId)).length > 1 ? 's' : ''}`,
      amount: initialPayload.allocations.reduce((total, { allocated }) => total - allocated, 0),
    };
  }

  getAddOnPayments(payments: ExecutedPaymentBatch, statusFilter: LoadableStatus): PaymentResultDisplay[] {
    if (!('addOns' in payments)) {
      return [];
    }

    return payments.addOns
      .filter(addOn => addOn.status === statusFilter)
      .map(addOn => addOn.data.chargeDetails.paymentDetails)
      .map(({ invoiceAddOnTypeId, amount }) => ({
        name: `${this.store.selectSnapshot(PaymentsState.getAddOnOption(invoiceAddOnTypeId)).type}`,
        amount,
      }))
      .sort((a, b) => compareStringsIgnoreCase(a.name, b.name));
  }

  navigateToSupportArticle(): void {
    window.open('https://www.clientsupport.aiwyn.ai/hc/en-us/articles/6467585256091-Failed-ACH-Payment', '_blank');
  }

  /**
   * PP-3820: recover from an expired session by reloading the page, which re-runs auth and re-mints a
   * fresh token. No-op unless the failure was a 401, so the shared confirm button stays a plain close
   * for every other error path.
   */
  onConfirm(): void {
    if (this.isSessionExpired) {
      window.location.reload();
    }
  }

  onLeave(): void {
    // closeWindow();
  }
}
